<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useThemeStore } from './stores/theme';

const theme = useThemeStore();
const route = useRoute();
void theme.load();

const LINKS = [
  { to: '/play/online', label: 'Play online' },
  { to: '/play/solo', label: 'Play solo' },
  { to: '/decks', label: 'Decks' },
  { to: '/how-to-play', label: 'How to play' },
  { to: '/settings', label: 'Settings' },
] as const;
const showDemo = import.meta.env.DEV;

/** Below 700px the links collapse behind a menu button; the menu closes on navigation and Escape. */
const menuOpen = ref(false);
watch(
  () => route.fullPath,
  () => {
    menuOpen.value = false;
  },
);
function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape' && menuOpen.value) menuOpen.value = false;
}
onMounted(() => window.addEventListener('keydown', onKey));
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <div class="shell">
    <header class="topbar">
      <RouterLink to="/" class="brand">Gwent</RouterLink>
      <button
        type="button"
        class="menu-toggle"
        :aria-expanded="menuOpen"
        aria-controls="site-nav"
        aria-label="Menu"
        @click="menuOpen = !menuOpen"
      >
        <span class="menu-toggle__bar"></span>
        <span class="menu-toggle__bar"></span>
        <span class="menu-toggle__bar"></span>
      </button>
      <nav id="site-nav" class="nav" :class="{ 'nav--open': menuOpen }" aria-label="Site">
        <RouterLink v-for="l in LINKS" :key="l.to" :to="l.to">{{ l.label }}</RouterLink>
        <RouterLink v-if="showDemo" to="/demo">Drag demo</RouterLink>
        <RouterLink to="/settings" class="theme-badge theme-badge--menu" :title="theme.resolved.layers.join(' + ')">
          theme: {{ theme.resolved.manifest.name }}
        </RouterLink>
      </nav>
      <RouterLink to="/settings" class="theme-badge theme-badge--bar" :title="theme.resolved.layers.join(' + ')">
        theme: {{ theme.resolved.manifest.name }}
      </RouterLink>
    </header>
    <main class="content">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
.shell {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.topbar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 0.6rem 1rem;
  background: var(--ui-panel);
  border-bottom: 1px solid color-mix(in srgb, var(--ui-accent) 40%, transparent);
}
.brand {
  font-family: var(--font-display);
  font-size: 1.3rem;
  color: var(--ui-accent);
  text-decoration: none;
}
.nav {
  display: flex;
  gap: 1rem;
}
.nav a {
  color: var(--ui-text);
  text-decoration: none;
  padding: 0.15rem 0;
  border-bottom: 2px solid transparent;
}
.nav a:hover {
  color: var(--ui-highlight);
}
.nav a.router-link-active {
  color: var(--ui-accent);
  border-bottom-color: var(--ui-accent);
}
.theme-badge {
  color: var(--ui-textMuted);
  font-size: 0.85rem;
  text-decoration: none;
}
.theme-badge--bar {
  margin-left: auto;
}
.theme-badge--bar:hover,
.theme-badge--bar:focus-visible {
  color: var(--ui-highlight);
}
.theme-badge--menu {
  display: none;
}
.menu-toggle {
  display: none;
  margin-left: auto;
  width: 2.5rem;
  height: 2.2rem;
  padding: 0.45rem 0.55rem;
  flex-direction: column;
  justify-content: space-between;
  background: transparent;
  border: 1px solid color-mix(in srgb, var(--ui-accent) 50%, transparent);
  border-radius: 4px;
  cursor: pointer;
}
.menu-toggle__bar {
  display: block;
  height: 2px;
  background: var(--ui-text);
  border-radius: 1px;
}
.content {
  flex: 1;
  min-height: 0;
}
@media (max-width: 700px) {
  .topbar {
    gap: 0.75rem;
  }
  .menu-toggle {
    display: flex;
  }
  .theme-badge--bar {
    display: none;
  }
  .nav {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 20;
    flex-direction: column;
    gap: 0;
    padding: 0.4rem 0;
    background: var(--ui-panel);
    border-bottom: 1px solid color-mix(in srgb, var(--ui-accent) 40%, transparent);
    box-shadow: 0 0.6rem 1.2rem color-mix(in srgb, var(--ui-background) 80%, transparent);
  }
  .nav--open {
    display: flex;
  }
  .nav a {
    padding: 0.65rem 1rem;
    border-bottom: 0;
  }
  .nav a.router-link-active {
    background: color-mix(in srgb, var(--ui-accent) 15%, transparent);
  }
  .theme-badge--menu {
    display: block;
    color: var(--ui-textMuted);
  }
}
</style>
